import React, { createContext, useContext, useState, type ReactNode } from "react";

export interface AppliedFilters {
  accountIds: string[];
  services: string[];
  regions: string[];
}

const emptyFilters: AppliedFilters = { accountIds: [], services: [], regions: [] };

export type DateMode = "month" | "range";

interface DashboardContextType {
  customer: string;
  setCustomer: (c: string) => void;
  // Date selection
  dateMode: DateMode;
  setDateMode: (m: DateMode) => void;
  month: string;          // YYYY-MM (used when dateMode=month)
  setMonth: (m: string) => void;
  startDate: string;      // YYYY-MM-DD (used when dateMode=range)
  setStartDate: (d: string) => void;
  endDate: string;        // YYYY-MM-DD (used when dateMode=range)
  setEndDate: (d: string) => void;
  // Convenience: returns the params to send to API
  dateParams: { month?: string; start_date?: string; end_date?: string };
  dateLabel: string;
  // Filters
  filters: AppliedFilters;
  setFilters: (f: AppliedFilters) => void;
  clearFilters: () => void;
  hasFilters: boolean;
}

const DashboardContext = createContext<DashboardContextType | null>(null);

function formatDateLabel(mode: DateMode, month: string, startDate: string, endDate: string): string {
  if (mode === "month") return month;
  return `${startDate} → ${endDate}`;
}

export function DashboardProvider({ children }: { children: ReactNode }) {
  const now = new Date();
  const [customer, setCustomer] = useState("");
  const [dateMode, setDateMode] = useState<DateMode>("month");
  const [month, setMonth] = useState(now.toISOString().slice(0, 7));
  const [startDate, setStartDate] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(now.toISOString().slice(0, 10));
  const [filters, setFilters] = useState<AppliedFilters>(emptyFilters);

  const clearFilters = () => setFilters(emptyFilters);
  const hasFilters = filters.accountIds.length > 0 || filters.services.length > 0 || filters.regions.length > 0;

  const dateParams = dateMode === "month"
    ? { month }
    : { start_date: startDate, end_date: endDate };

  const dateLabel = formatDateLabel(dateMode, month, startDate, endDate);

  return (
    <DashboardContext.Provider value={{
      customer, setCustomer,
      dateMode, setDateMode, month, setMonth, startDate, setStartDate, endDate, setEndDate,
      dateParams, dateLabel,
      filters, setFilters, clearFilters, hasFilters,
    }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error("useDashboard must be inside DashboardProvider");
  return ctx;
}
